// WorkspacesNavToggle — client-side nav panel toggle (ts-5).
//
// Replaces the server-side `workspaces_toggle` handler in
// workspaces_nav_hook.ex.
//
// Markup convention:
//   Attach phx-hook="WorkspacesNavToggle" to the toggle trigger button.
//   The workspaces panel: id="workspaces-nav-panel"
//   Other nav panels: [data-nav-panel] (profile panel, search panel, etc.)
//
// Opening the workspaces panel closes all other nav panels by removing
// their `data-open` attribute.
//
// The panel element is server-rendered: a LiveView patch of its region
// strips the client-set `data-open` attribute and the panel used to
// slam shut moments after the user opened it. Open state therefore
// lives in the shared per-panel store (./panel_state) — which also
// keeps multiple triggers (mobile + desktop nav) driving one truth —
// and `_panelOpen` on the hook is a display mirror refreshed on every
// interaction.

import type { LiveViewHook } from "./types";
import { attachPanel, type SharedPanel } from "./panel_state";

export interface WorkspacesNavToggleState {
  _onClick: () => void;
  _panelOpen: boolean;
  _panel: SharedPanel;
}

const applyOpen = (open: boolean, panel: HTMLElement): void => {
  if (open) {
    panel.setAttribute("data-open", "");
  } else {
    panel.removeAttribute("data-open");
  }
};

const domMatches = (open: boolean, panel: HTMLElement): boolean =>
  panel.hasAttribute("data-open") === open;

export const WorkspacesNavToggle = {
  mounted(this: LiveViewHook<never> & WorkspacesNavToggleState) {
    this._panel = attachPanel(
      document.getElementById("workspaces-nav-panel"),
      applyOpen,
      domMatches,
    );
    this._panelOpen = this._panel.isOpen();

    const onClick = (): void => {
      // Exclusive: opening (or closing) the workspaces panel closes
      // every other nav panel first.
      document
        .querySelectorAll<HTMLElement>("[data-nav-panel]")
        .forEach((p) => {
          p.removeAttribute("data-open");
        });
      this._panelOpen = this._panel.toggle();
    };
    this.el.addEventListener("click", onClick);
    this._onClick = onClick;
  },

  updated(this: LiveViewHook<never> & WorkspacesNavToggleState) {
    this._panel.enforce();
    this._panelOpen = this._panel.isOpen();
  },

  destroyed(this: LiveViewHook<never> & WorkspacesNavToggleState) {
    this.el.removeEventListener("click", this._onClick as EventListener);
    this._panel.disconnect();
  },
};
